import type { TimeRangeFinite } from "types/types";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { midgardClients } from "providers/MidgardProvider";
import { useState, useTransition } from "react";
import { useMount } from "constants/constants";
import { ONE_HOUR_OF_MS } from "utils/timeUtils";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useQueryAndUpdateAllocationList } from "./supervisorAllocationGetterUtils";
import {
  FAST_RETRY_INTERVAL,
  ALLOCATION_FETCH_INTERVAL,
} from "constants/intervals";
import { useDispatch } from "react-redux";
import { clearOneAllocationList } from "providers/GlobalStateProvider";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

type SupervisorAllocationGetterProps = {
  timeRange: TimeRangeFinite | null;
  processIds: string[];
  initialPollInterval?: number;
};

export default function SupervisorAllocationGetter({
  timeRange,
  processIds,
  initialPollInterval = ALLOCATION_FETCH_INTERVAL,
}: SupervisorAllocationGetterProps) {
  const dispatch = useDispatch();
  const ckey = useCustomerKey();

  const client = midgardClients.get(ckey);
  const [pollInterval, setPollInterval] = useState(initialPollInterval);
  const [, startTransition] = useTransition();
  const [isMounted, setIsMounted] = useState(true);
  useMount(() => {
    return () => {
      setIsMounted(false);

      // when SupervisorAllocationGetter is unmounted,
      // clear allocation list for time range
      if (timeRange) {
        startTransition(() => {
          dispatch(clearOneAllocationList(timeRange, processIds));
        });
      }
    };
  });

  const timeRangeDuration = timeRange ? timeRange.lt - timeRange.ge : 0;

  const isTimeRangeInverted = timeRangeDuration < 0;
  const isTimeRangeZero = timeRangeDuration === 0;
  if (isTimeRangeInverted) {
    console.error(
      `DataDumpGetter time range is inverted! location.pathname: ${
        window.location.pathname
      }, timeRange: ${JSON.stringify(timeRange)} (${(
        timeRangeDuration / ONE_HOUR_OF_MS
      ).toFixed(1)} hours), focusRange: ${JSON.stringify(timeRange)}`
    );
  }

  const isStorybook = useIsStorybook();
  const shouldSkipQuery =
    isStorybook ||
    !isMounted ||
    !timeRange ||
    isTimeRangeInverted ||
    isTimeRangeZero;

  return shouldSkipQuery ? null : (
    <Query
      {...{
        timeRange,
        processIds,
        isMounted,
        setPollInterval,
        ckey,
        client,
        pollInterval,
        initialPollInterval,
      }}
    />
  );
}

function Query({
  timeRange,
  processIds,
  isMounted,
  setPollInterval,
  ckey,
  client,
  pollInterval,
  initialPollInterval,
}: any) {
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();
  useQueryAndUpdateAllocationList({
    variables: {
      ge: timeRange ? timeRange.ge.toString() : "0",
      lt: timeRange ? timeRange.lt.toString() : "0",
      machemetryIds: processIds,
    },
    onError: err => {
      // if we get an error, try again in 10 seconds
      if (isMounted) {
        setPollInterval(FAST_RETRY_INTERVAL);
        console.log(
          `Allocation list query gone wrong. cKey: ${ckey}, ge: ${timeRange?.ge}, lt: ${timeRange?.lt}, machemetryIds: ${processIds}`
        );
        console.log(`retrying in ${FAST_RETRY_INTERVAL / 1000}s`);
      }
    },
    client,
    timeRange,
    pollInterval: pollInterval * multiplyNetworkRequestIntervals,
    initialPollInterval,
    setPollInterval,
  });

  return null;
}
